import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { RadioButton, RadioGroup, Icon } from '@cbrebuild/blocks';

import FileUploadAccordian from '../../file-upload-accordian';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';
import '../../lease-abstraction.scss';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import useFormState, { EVENT_VALUE_TYPES } from '../../../../hooks/useFormState';
import NumberInput from '../../../../nucleus/inputs/NumberInput';
import { updateCompData } from '../../../../redux/actions/voucher/comp-data-actions';
import Tooltip from '../../../../nucleus/tooltip/tooltip';

const expenseTypeOverrides = {
  parking_expenses_amount_per_space: EVENT_VALUE_TYPES.FLOAT,
  operating_expenses_amount_per_sqft: EVENT_VALUE_TYPES.FLOAT,
  tax_amount: EVENT_VALUE_TYPES.FLOAT,
  electric_expense_amount_per_sqft: EVENT_VALUE_TYPES.FLOAT,
};

const tenantImprovementDetailOptions = [
  { display_name: 'Allowance', value: 'Allowance' },
  { display_name: 'As Is', value: 'As Is' },
  { display_name: 'Spec Suite', value: 'Spec Suite' },
  { display_name: 'Turn Key', value: 'Turn Key' },
];

const Expenses = ({ storageKey }) => {
  const {
    isEnhancedFlow,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    validateLeaseAbstractionFields,
    showPageValidation,
    fieldErrorMap,
    saveCompField,
    clearFieldError,
    isEarlyInvoice,
  } = useContext(LeaseAbstractionContext);

  const dispatch = useDispatch();
  const compData = useSelector(state => state.compData);
  const showRequiredValidation = showPageValidation && !isEarlyInvoice;

  const {
    parking_expenses_amount_per_space,
    operating_expenses_amount_per_sqft,
    tax_amount,
    electric_included_in_operating_expenses,
    electric_expense_amount_per_sqft,
    onStateChange,
    onStateChanged,
  } = useFormState({
    parking_expenses_amount_per_space: compData?.parking_expenses_amount_per_space,
    operating_expenses_amount_per_sqft: compData?.operating_expenses_amount_per_sqft,
    tax_amount: compData?.tax_amount,
    electric_included_in_operating_expenses: compData?.electric_included_in_operating_expenses,
    electric_expense_amount_per_sqft: compData?.electric_expense_amount_per_sqft,
  }, changes => dispatch(updateCompData(compData.id, changes)), expenseTypeOverrides);

  const infoTooltip = (
    <Tooltip message="$0.00 is a valid value.">
      <Icon iconName="info-circle" />
    </Tooltip>
  );

  const electricExpenseIsRequired = isEnhancedFlow && !isEarlyInvoice
    && (electric_included_in_operating_expenses.value === false
      || electric_included_in_operating_expenses.value === 'false');

  return (
    <FileUploadAccordian accordianTitle="Expenses" storageKey={storageKey}>
      {/* Legacy "Import Data to Voucher" modal flow — only the original 3 fields,
          stacked single-column. The extras (Parking Expenses, Operating Expenses,
          Taxes, electric questions) are new flow only. */}
      {!isEnhancedFlow && (
        <>
          <LeaseAbstractionField
            key="tenantImprovementAllowance"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.tenantImprovementAllowance,
              label: 'Tenant Improvement Allowance ($/SqFt)',
              errorMessage: fieldErrorMap.tenantImprovementAllowance,
              key: 'tenantImprovementAllowance',
            }}
            fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d{0,11}(\.\d{0,2})?$/;
              if (regex.test(newValue) || newValue === '') {
                modalFieldChangeHandler('tenantImprovementAllowance', { value: newValue });
              }
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantImprovementAllowance.trackingID,
                stateVariable: 'tenantImprovementAllowance',
              });
            }}
          />
          <LeaseAbstractionField
            key="parkingRatio"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.parkingRatio,
              label: 'Parking Ratio (spots per 1000 SqFt)',
              errorMessage: fieldErrorMap.parkingRatio,
              key: 'parkingRatio',
            }}
            fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d{0,11}(\.\d{0,3})?$/;
              if (regex.test(newValue) || newValue === '') {
                modalFieldChangeHandler('parkingRatio', { value: newValue });
              }
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.parkingRatio.trackingID,
                stateVariable: 'parkingRatio',
              });
            }}
            validateLeaseAbstractionFields={validateLeaseAbstractionFields}
          />
          <LeaseAbstractionField
            key="tenantImprovementDetails"
            fieldType={LeaseAbstractionFieldType.SELECT}
            fieldData={{
              data: modalFieldState.tenantImprovementDetails,
              label: 'Tenant Improvement Details',
              errorMessage: fieldErrorMap.tenantImprovementDetails,
              options: tenantImprovementDetailOptions,
              key: 'tenantImprovementDetails',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('tenantImprovementDetails', { value: event.value });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantImprovementDetails.trackingID,
                stateVariable: 'tenantImprovementDetails',
              });
            }}
          />
        </>
      )}

      {/* New enhanced split-pane flow — original three fields plus all new fields,
          laid out in two-column rows. */}
      {isEnhancedFlow && (
        <>
          {/* Row 1: Tenant Improvement Allowance | Parking Ratio */}
          <div className="acc-field-row">
            <div className="acc-field-col">
              <LeaseAbstractionField
                key="tenantImprovementAllowance"
                fieldType={LeaseAbstractionFieldType.TEXT}
                isRequired={!isEarlyInvoice}
                fieldData={{
              data: modalFieldState.tenantImprovementAllowance,
              label: 'Tenant Improvement Allowance ($/SqFt)',
              errorMessage: (!modalFieldState.tenantImprovementAllowance.value && showRequiredValidation)
                ? 'Tenant Improvement Allowance ($/SF) is required'
                : fieldErrorMap.tenantImprovementAllowance,
              key: 'tenantImprovementAllowance',
            }}
                fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d{0,11}(\.\d{0,2})?$/;
              if (regex.test(newValue) || newValue === '') {
                modalFieldChangeHandler('tenantImprovementAllowance', {
                  value: newValue,
                });
              }
            }}
                onBlur={() => saveCompField('tenantImprovementAllowance', modalFieldState.tenantImprovementAllowance.value)}
                acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('tenantImprovementAllowance', { value: String(extractedVal) }, 'single');
              clearFieldError('tenantImprovementAllowance');
              saveCompField('tenantImprovementAllowance', extractedVal);
            }}
                individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantImprovementAllowance.trackingID,
                stateVariable: 'tenantImprovementAllowance',
              });
            }}
                validateLeaseAbstractionFields={validateLeaseAbstractionFields}
              />
            </div>
            <div className="acc-field-col">
              <LeaseAbstractionField
                key="parkingRatio"
                fieldType={LeaseAbstractionFieldType.TEXT}
                fieldData={{
              data: modalFieldState.parkingRatio,
              label: 'Parking Ratio (spots per 1000 SqFt)',
              errorMessage: fieldErrorMap.parkingRatio,
              key: 'parkingRatio',
            }}
                fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d{0,11}(\.\d{0,3})?$/;
              if (regex.test(newValue) || newValue === '') {
                modalFieldChangeHandler('parkingRatio', { value: newValue });
              }
            }}
                onBlur={() => saveCompField('parkingRatio', modalFieldState.parkingRatio.value)}
                acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('parkingRatio', { value: String(extractedVal) }, 'single');
              clearFieldError('parkingRatio');
              saveCompField('parkingRatio', extractedVal);
            }}
                individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.parkingRatio.trackingID,
                stateVariable: 'parkingRatio',
              });
            }}
                validateLeaseAbstractionFields={validateLeaseAbstractionFields}
              />
            </div>
          </div>

          {/* Row 2: Tenant Improvement Details | Parking Expenses */}
          <div className="acc-field-row">
            <div className="acc-field-col">
              <LeaseAbstractionField
                key="tenantImprovementDetails"
                fieldType={LeaseAbstractionFieldType.SELECT}
                isRequired={!isEarlyInvoice}
                fieldData={{
              data: modalFieldState.tenantImprovementDetails,
              label: 'Tenant Improvement Details',
              errorMessage: (!modalFieldState.tenantImprovementDetails.value && showRequiredValidation)
                ? 'Tenant Improvements Detail is required'
                : fieldErrorMap.tenantImprovementDetails,
              options: tenantImprovementDetailOptions,
              key: 'tenantImprovementDetails',
            }}
                fieldChangeHandler={(event) => {
              modalFieldChangeHandler('tenantImprovementDetails', {
                value: event.value,
              });
              saveCompField('tenantImprovementDetails', event.value);
            }}
                acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('tenantImprovementDetails', { value: extractedVal }, 'single');
              clearFieldError('tenantImprovementDetails');
              saveCompField('tenantImprovementDetails', extractedVal);
            }}
                individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantImprovementDetails.trackingID,
                stateVariable: 'tenantImprovementDetails',
              });
            }}
                validateLeaseAbstractionFields={validateLeaseAbstractionFields}
              />
            </div>
            <div className="acc-field-col">
              <div className="file-modal-group-container">
                <div className="file-modal-group">
                  <label className="acc-field-label">Parking Expenses ($/Space)</label>
                  <NumberInput
                    name="parking_expenses_amount_per_space"
                    precision={2}
                    integerPrecision={11}
                    value={parking_expenses_amount_per_space.value}
                    onChange={onStateChange}
                    onBlur={onStateChanged}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Operating Expenses | Taxes */}
          <div className="acc-field-row">
            <div className="acc-field-col">
              <div className="file-modal-group-container">
                <div className="file-modal-group">
                  <label className="acc-field-label">
                    Operating Expenses ($/SqFt per year)
                    {infoTooltip}
                  </label>
                  <NumberInput
                    name="operating_expenses_amount_per_sqft"
                    value={operating_expenses_amount_per_sqft.value}
                    precision={2}
                    integerPrecision={11}
                    onChange={onStateChange}
                    onBlur={onStateChanged}
                  />
                </div>
              </div>
            </div>
            <div className="acc-field-col">
              <div className="file-modal-group-container">
                <div className="file-modal-group">
                  <label className="acc-field-label">
                    Taxes ($/SqFt per year)
                    {infoTooltip}
                  </label>
                  <NumberInput
                    name="tax_amount"
                    value={tax_amount.value}
                    precision={2}
                    integerPrecision={11}
                    onChange={onStateChange}
                    onBlur={onStateChanged}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Is electric included in operating expenses? */}
          <div className="file-modal-group-container">
            <div className="file-modal-group">
              <legend className="secondary-legend">Is electric included in the operating expenses?</legend>
              <RadioGroup
                name="electric_included_in_operating_expenses"
                className="radioVal"
                selectedValue={electric_included_in_operating_expenses.value?.toString()}
                onChange={onStateChanged}
                orientation="vertical"
              >
                <RadioButton value="true">Yes, electric is included</RadioButton>
                <RadioButton value="false">No</RadioButton>
              </RadioGroup>
            </div>
          </div>

          {/* Electric Expenses */}
          <div className="file-modal-group-container">
            <div className="file-modal-group">
              <label className="acc-field-label">
                {electricExpenseIsRequired && <span className="acc-required-asterisk">* </span>}
                Electric Expenses ($/SqFt per year)
                {infoTooltip}
              </label>
              <NumberInput
                name="electric_expense_amount_per_sqft"
                value={electric_expense_amount_per_sqft.value}
                precision={2}
                integerPrecision={11}
                invalid={showPageValidation && electricExpenseIsRequired && electric_expense_amount_per_sqft.value == null}
                onChange={onStateChange}
                onBlur={onStateChanged}
              />
              {showPageValidation && electricExpenseIsRequired && electric_expense_amount_per_sqft.value == null && (
              <p className="acc-error-message">Electric Expenses ($/SF per year) is required</p>
          )}
            </div>
          </div>
        </>
      )}
    </FileUploadAccordian>
  );
};

Expenses.propTypes = {
  storageKey: PropTypes.string,
};

Expenses.defaultProps = {
  storageKey: '',
};

export default Expenses;
